import {
  hasAllPermissions,
  type PermissionCode,
  type PermissionSession
} from '@super-mega/server/contracts/authorization'

type RouteRule = { pattern: RegExp; permissions: readonly PermissionCode[] }

/** 更具体的子页面先匹配；路由权限独立于业务权限。公开页面不会经过此目录。 */
export const privateRouteRules: readonly RouteRule[] = [
  {
    pattern: /^\/customer-management\/customers\/\d+\/collection-policy$/,
    permissions: ['route.customers', 'customer.read', 'customer.billing.update']
  },
  { pattern: /^\/system-management\/bots(?:\/[0-9a-fA-F-]{36})?$/, permissions: ['route.bots', 'bot.read'] },
  { pattern: /^\/customer-management\/customers\/new$/, permissions: ['route.customers', 'customer.create'] },
  { pattern: /^\/customer-management\/customers\/\d+\/edit$/, permissions: ['route.customers', 'customer.update'] },
  {
    pattern: /^\/customer-management\/customers\/\d+\/documents$/,
    permissions: ['route.customers', 'customer.read', 'contract.document.read']
  },
  { pattern: /^\/customer-management\/customers(?:\/\d+)?$/, permissions: ['route.customers', 'customer.read'] },
  { pattern: /^\/customer-management\/calendar$/, permissions: ['route.customerCalendar', 'customer.read'] },
  { pattern: /^\/customer-management\/customer-tags$/, permissions: ['route.customerTags', 'customer.tag.read'] },
  {
    pattern: /^\/customer-management\/subjects\/materials\/batch$/,
    permissions: ['route.subjects', 'subject.material.update']
  },
  { pattern: /^\/customer-management\/subjects$/, permissions: ['route.subjects', 'subject.read'] },
  { pattern: /^\/orders\/rentals\/(?:new|batch-new)$/, permissions: ['route.orders.rentals', 'order.rental.create'] },
  { pattern: /^\/orders\/rentals\/edit\/\d+$/, permissions: ['route.orders.rentals', 'order.rental.update'] },
  {
    pattern: /^\/orders\/rentals\/materials\/batch$/,
    permissions: ['route.orders.rentals', 'order.rental.material.update']
  },
  {
    pattern: /^\/orders\/rentals\/contract\/\d+$/,
    permissions: ['route.orders.rentals', 'order.rental.read', 'contract.document.read']
  },
  {
    pattern: /^\/orders\/rentals\/contracts\/batch$/,
    permissions: ['route.orders.rentals', 'order.rental.read', 'contract.document.create', 'contract.document.confirm']
  },
  { pattern: /^\/orders\/rentals(?:\/\d+)?$/, permissions: ['route.orders.rentals', 'order.rental.read'] },
  { pattern: /^\/orders\/service\/new$/, permissions: ['route.orders.service', 'order.service.create'] },
  { pattern: /^\/orders\/service\/\d+\/edit$/, permissions: ['route.orders.service', 'order.service.update'] },
  {
    pattern: /^\/orders\/service\/contract\/\d+$/,
    permissions: ['route.orders.service', 'order.service.read', 'contract.document.read']
  },
  {
    pattern: /^\/orders\/service\/contracts\/batch$/,
    permissions: ['route.orders.service', 'order.service.read', 'contract.document.create', 'contract.document.confirm']
  },
  { pattern: /^\/orders\/service(?:\/\d+)?$/, permissions: ['route.orders.service', 'order.service.read'] },
  { pattern: /^\/orders\/groups(?:\/\d+)?$/, permissions: ['route.orders.groups', 'order.group.read'] },
  { pattern: /^\/orders\/contracts\/print$/, permissions: ['contract.document.read'] },
  {
    pattern: /^\/finance\/groups\/receipt-batch$/,
    permissions: ['route.finance.groups', 'bill.group.read', 'bill.read', 'bill.receipt.record']
  },
  { pattern: /^\/finance\/groups$/, permissions: ['route.finance.groups', 'bill.group.read'] },
  { pattern: /^\/finance\/receipts$/, permissions: ['route.finance.groups', 'bill.group.read'] },
  { pattern: /^\/finance\/bills$/, permissions: ['route.finance.bills', 'bill.read'] },
  { pattern: /^\/finance\/expenses$/, permissions: ['route.finance.expenses', 'expense.read'] },
  { pattern: /^\/finance\/money$/, permissions: ['route.finance.money', 'money.read'] },
  { pattern: /^\/finance$/, permissions: [] },
  { pattern: /^\/contracts\/templates\/new$/, permissions: ['route.contracts.templates', 'contract.template.create'] },
  {
    pattern: /^\/contracts\/templates\/versions\/\d+$/,
    permissions: ['route.contracts.templates', 'contract.template.read']
  },
  { pattern: /^\/contracts\/templates\/\d+$/, permissions: ['route.contracts.templates', 'contract.template.update'] },
  { pattern: /^\/contracts\/templates$/, permissions: ['route.contracts.templates', 'contract.template.read'] },
  {
    pattern: /^\/contracts\/edit\/\d+$/,
    permissions: ['contract.document.read', 'contract.document.update']
  },
  {
    pattern: /^\/contracts\/(?:documents|view\/\d+)$/,
    permissions: ['route.contracts.documents', 'contract.document.read']
  },
  { pattern: /^\/contracts\/variables$/, permissions: ['route.contracts.variables'] },
  {
    pattern: /^\/address\/rooms\/\d+\/rentals$/,
    permissions: ['route.address.rooms', 'room.read', 'order.rental.read']
  },
  { pattern: /^\/address\/rooms$/, permissions: ['route.address.rooms', 'room.read'] },
  { pattern: /^\/address\/sites$/, permissions: ['route.address.sites', 'site.read'] },
  { pattern: /^\/licenses$/, permissions: ['route.licenses', 'company.read', 'identity.read'] },
  { pattern: /^\/master-data\/businesses$/, permissions: ['route.masterData.businesses', 'business.read'] },
  {
    pattern: /^\/master-data\/expense-businesses$/,
    permissions: ['route.masterData.expenseBusinesses', 'expenseBusiness.read']
  },
  {
    pattern: /^\/master-data\/legal-entities\/new$/,
    permissions: ['route.masterData.organizations', 'organization.create']
  },
  {
    pattern: /^\/master-data\/legal-entities\/\d+\/edit$/,
    permissions: ['route.masterData.organizations', 'organization.update']
  },
  { pattern: /^\/master-data\/legal-entities$/, permissions: ['route.masterData.organizations', 'organization.read'] },
  { pattern: /^\/access\/members$/, permissions: ['route.access.members', 'member.read'] },
  { pattern: /^\/access\/accounts$/, permissions: ['route.access.accounts', 'account.read'] },
  { pattern: /^\/access\/applications$/, permissions: ['route.access.accounts', 'account.application.read'] },
  {
    pattern: /^\/system-management\/roles(?:\/(?:new|[0-9a-fA-F-]{36}))?$/,
    permissions: ['route.system.roles', 'role.read']
  },
  { pattern: /^\/reports$/, permissions: ['route.reports.overview', 'report.read'] },
  ...(['organizations', 'customers', 'salespeople', 'sites', 'businesses', 'subjects'] as const).map((dimension) => ({
    pattern: new RegExp(`^/reports/${dimension}$`),
    permissions: [`route.reports.${dimension}`, 'report.read'] as const
  })),
  { pattern: /^\/(?:home|unauthorized)$/, permissions: [] }
]

export function routeRequirements(pathname: string) {
  const normalized = pathname.replace(/\/+$/, '') || '/'
  return privateRouteRules.find((rule) => rule.pattern.test(normalized))?.permissions
}

export function canVisitRoute(session: PermissionSession, pathname: string) {
  const required = routeRequirements(pathname)
  return required !== undefined && hasAllPermissions(session, required)
}
